import React, { useEffect } from 'react';
import {
  ArrowLeft,
  FileText,
  Video,
  ExternalLink,
  Users,
  Clock,
  Edit,
} from 'lucide-react';
import './LessonViewer.css';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import {
  DocumentViewer,
  VideoViewer,
  ScormViewer,
  UrlViewer,
  ILTSViewer,
} from './Components';
import useSuperAdminContentManagementStore from '@/stores/SuperAdminStore/SuperAdminContentManagementStore';
import { StandaloneLessonResponse } from '@/services/SuperAdminLessonService';
import { getLessonContentBaseUrl } from '@/utils/assetUrl';
import { useScormUrl } from '@/hooks/useScormUrl';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5111';

interface LessonViewerProps {
  lessonId: number;
  onClose: () => void;
  lessonData?: StandaloneLessonResponse | null;
}

export default function LessonViewer({ lessonId, onClose, lessonData }: LessonViewerProps) {
  const {
    selectedLesson: storeLesson,
    loading,
    error,
    fetchStandaloneLessonById,
  } = useSuperAdminContentManagementStore();

  // Use passed lesson data if available, otherwise use store lesson
  const lesson = lessonData || storeLesson;

  // Fetch dynamic SCORM URL if lesson is SCORM type
  const { scormUrl } = useScormUrl(
    lesson?.contentType === 'scorm' ? (lesson.lessonContentScorm || undefined) : undefined
  );

  // Fetch lesson data only if not provided and not already in store
  useEffect(() => {
    if (lessonId && !lessonData && (!lesson || lesson.id !== lessonId)) {
      console.log('LessonViewer - Fetching standalone lesson:', lessonId);
      fetchStandaloneLessonById(lessonId);
    }
  }, [lessonId]);

  const getContentUrl = () => {
    if (!lesson) {
      console.log('LessonViewer - No lesson data');
      return '';
    }

    console.log('LessonViewer - Full lesson data:', JSON.stringify(lesson, null, 2));
    console.log('LessonViewer - Lesson type:', lesson.lessonType);
    console.log('LessonViewer - Content type:', lesson.contentType);
    console.log('LessonViewer - SOCKET_URL:', SOCKET_URL);

    const baseUrl = getLessonContentBaseUrl();  // Use relative URLs to leverage proxy

    switch (lesson.contentType) {
      case 'document':
        console.log('LessonViewer - Document file:', lesson.lessonContentDocument);
        const docUrl = lesson.lessonContentDocument
          ? `${baseUrl}/documents/${lesson.lessonContentDocument}`
          : '';
        console.log('LessonViewer - Document URL:', docUrl);
        return docUrl;
      case 'mp4':
        console.log('LessonViewer - MP4 file:', lesson.lessonContentMp4);
        const mp4Url = lesson.lessonContentMp4
          ? `${baseUrl}/mp4/${lesson.lessonContentMp4}`
          : '';
        console.log('LessonViewer - MP4 URL:', mp4Url);
        return mp4Url;
      case 'scorm':
        console.log('LessonViewer - SCORM file:', lesson.lessonContentScorm);
        console.log('LessonViewer - SCORM URL (dynamic):', scormUrl);
        return scormUrl || '';
      case 'url':
        console.log('LessonViewer - URL:', lesson.lessonContentUrl);
        return lesson.lessonContentUrl || '';
      default:
        console.log('LessonViewer - Unknown content type, returning empty');
        return '';
    }
  };

  const renderContent = () => {
    if (!lesson) {
      console.log('renderContent - No lesson data');
      return null;
    }

    console.log('renderContent - Rendering content for lesson type:', lesson.lessonType);

    // ILTS Lesson
    if (lesson.lessonType === 'ILTS' && lesson.iltsInfo) {
      console.log('renderContent - Rendering ILTS viewer');
      return (
        <ILTSViewer
          iltsType={lesson.iltsInfo.mode || 'Online'}
          startDate={lesson.iltsInfo.startDate || undefined}
          startTime={lesson.iltsInfo.startTime || undefined}
          endDate={lesson.iltsInfo.endDate || undefined}
          endTime={lesson.iltsInfo.endTime || undefined}
          eventVenue={lesson.iltsInfo.venue || undefined}
          meetUrl={lesson.iltsInfo.meetUrl || undefined}
          description={lesson.description || undefined}
        />
      );
    }

    // Content-Based Lesson
    const contentUrl = getContentUrl();
    console.log('renderContent - Content URL:', contentUrl);

    if (!contentUrl) {
      console.log('renderContent - No content URL, showing no content message');
      return (
        <div className="s-admin-lesson-viewer__no-content">
          <FileText size={48} color="#9ca3af" />
          <p>No content available for this lesson</p>
        </div>
      );
    }

    // Render based on content type
    console.log('renderContent - Rendering viewer for content type:', lesson.contentType);
    switch (lesson.contentType) {
      case 'document':
        console.log('renderContent - Rendering DocumentViewer');
        return <DocumentViewer contentUrl={contentUrl} title={lesson.title} />;

      case 'mp4':
        console.log('renderContent - Rendering VideoViewer');
        return <VideoViewer contentUrl={contentUrl} title={lesson.title} />;

      case 'scorm':
        console.log('renderContent - Rendering ScormViewer');
        return <ScormViewer contentUrl={contentUrl} title={lesson.title} />;

      case 'url':
        console.log('renderContent - Rendering UrlViewer');
        return <UrlViewer contentUrl={contentUrl} title={lesson.title} />;

      default:
        console.log('renderContent - Unsupported content type:', lesson.contentType);
        return (
          <div className="s-admin-lesson-viewer__no-content">
            <FileText size={48} color="#9ca3af" />
            <p>Unsupported content type</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="s-admin-lesson-viewer">
        <LoadingSpinner fullScreen size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="s-admin-lesson-viewer">
        <ErrorDisplay
          fullScreen
          title="Failed to Load Lesson"
          message={error}
          type="error"
          showRetry
          onRetry={() => fetchStandaloneLessonById(lessonId)}
        />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="s-admin-lesson-viewer">
        <ErrorDisplay
          fullScreen
          title="Lesson Not Found"
          message="The lesson you're looking for doesn't exist."
          type="error"
        />
      </div>
    );
  }

  return (
    <div className="s-admin-lesson-viewer">
      {/* Header */}
      <div className="s-admin-lesson-viewer__header">
        <button onClick={onClose} className="s-admin-lesson-viewer__back-btn">
          <ArrowLeft size={20} />
          {lessonData ? 'Back to Details' : 'Back to Library'}
        </button>
        <div className="s-admin-lesson-viewer__breadcrumb">
          <span>Content Management</span>
          <span className="s-admin-lesson-viewer__breadcrumb-separator">›</span>
          <span>Library</span>
          <span className="s-admin-lesson-viewer__breadcrumb-separator">›</span>
          <span className="s-admin-lesson-viewer__breadcrumb-current">{lesson.title}</span>
        </div>
      </div>

      {/* Lesson Info */}
      <div className="s-admin-lesson-viewer__info">
        <div className="s-admin-lesson-viewer__info-left">
          <h1 className="s-admin-lesson-viewer__title">{lesson.title}</h1>
          <div className="s-admin-lesson-viewer__meta">
            {lesson.lessonType === 'Content-Based' && lesson.contentType && (
              <span className="s-admin-lesson-viewer__badge">
                {lesson.contentType === 'mp4' && <Video size={14} />}
                {lesson.contentType === 'document' && <FileText size={14} />}
                {lesson.contentType === 'scorm' && <FileText size={14} />}
                {lesson.contentType === 'url' && <ExternalLink size={14} />}
                {lesson.contentType.toUpperCase()}
              </span>
            )}
            {lesson.lessonType === 'ILTS' && lesson.iltsInfo && (
              <span className="s-admin-lesson-viewer__badge lesson-viewer__badge--ilts">
                <Users size={14} />
                {lesson.iltsInfo.mode} Session
              </span>
            )}
            {lesson.lessonDuration && (
              <span className="s-admin-lesson-viewer__duration">
                <Clock size={14} />
                {lesson.lessonDuration}
              </span>
            )}
          </div>
        </div>
        {!lessonData && (
          <button className="s-admin-lesson-viewer__edit-btn" onClick={() => console.log('Edit lesson')}>
            <Edit size={18} />
            Edit Lesson
          </button>
        )}
      </div>

      {/* Content Area */}
      <div className="s-admin-lesson-viewer__content">{renderContent()}</div>

      {/* Description */}
      {lesson.description && lesson.lessonType === 'Content-Based' && (
        <div className="s-admin-lesson-viewer__description">
          <h3>Description</h3>
          <p>{lesson.description}</p>
        </div>
      )}
    </div>
  );
}
